// Widget data: mencatat dengan turus, menyusun diagram batang, membaca diagram batang,
// dan melengkapi piktogram.
import { icoSVG } from '../../subjects/matematika/gambar.js';
import { seret, titikSVG, batas, goyang, letup, animasi, jeda } from '../alat.js';

const WARNA = ['var(--pink)', 'var(--sky)', 'var(--sun)', 'var(--mint)', 'var(--grape)', 'var(--orange)'];

// Turus: kelompok lima = empat garis tegak + satu garis miring.
export function turusSVG(n) {
  const grup = Math.ceil(n / 5) || 0, w = Math.max(1, grup) * 46;
  let g = '';
  for (let k = 0; k < n; k++) {
    const gi = Math.floor(k / 5), j = k % 5, x0 = gi * 46 + 6;
    g += j < 4 ? `<line x1="${x0 + j * 8}" y1="4" x2="${x0 + j * 8}" y2="30" class="tr-garis" style="--d:${k}"/>`
      : `<line x1="${x0 - 4}" y1="26" x2="${x0 + 28}" y2="8" class="tr-garis miring" style="--d:${k}"/>`;
  }
  return `<svg class="tr-svg" viewBox="0 0 ${w} 34" width="${w}" height="34" aria-hidden="true">${g}</svg>`;
}

/**
 * Catat jawaban satu per satu dengan turus, lalu lihat tabel frekuensinya.
 * param: { kategori: [{id, nama}], data: [id, id, ...], judul }
 */
export function turus(host, p, ctx) {
  const st = { i: 0, hit: Object.fromEntries(p.kategori.map(k => [k.id, 0])) };
  host.innerHTML = `<div class="wg wg-turus">
    <div class="tr-antre" aria-live="polite"></div>
    <table class="tbl tr-tabel"><caption>${p.judul}</caption>
      <thead><tr><th>${p.kolom || 'Pilihan'}</th><th>Turus</th><th>Frekuensi</th></tr></thead>
      <tbody>${p.kategori.map((k, j) => `<tr data-k="${k.id}"><td><button type="button" class="tr-pilih" data-k="${k.id}" style="--w:${WARNA[j % WARNA.length]}"><i></i>${k.nama}</button></td><td class="tr-garis-sel"></td><td class="tr-frek">?</td></tr>`).join('')}</tbody>
    </table>
  </div>`;
  const antre = host.querySelector('.tr-antre');
  const nama = id => p.kategori.find(k => k.id === id).nama;
  const tampilAntre = () => {
    if (st.i >= p.data.length) { antre.innerHTML = '<b>Semua jawaban sudah dicatat!</b>'; return; }
    antre.innerHTML = `<span class="muted">Jawaban ke-${st.i + 1} dari ${p.data.length}:</span><span class="tr-kartu">${nama(p.data[st.i])}</span><span class="tr-sisa">${p.data.slice(st.i + 1, st.i + 6).map(() => '<i></i>').join('')}</span>`;
    animasi(antre.querySelector('.tr-kartu'), [{ transform: 'translateY(-14px) rotate(-6deg)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 300 });
  };
  const selesai = async () => {
    for (const k of p.kategori) {
      const sel = host.querySelector(`tr[data-k="${k.id}"] .tr-frek`);
      sel.textContent = st.hit[k.id];
      letup(sel);
      await jeda(180);
    }
    const maks = Math.max(...Object.values(st.hit));
    const modus = p.kategori.filter(k => st.hit[k.id] === maks).map(k => k.nama).join(' dan ');
    host.querySelectorAll('.tr-pilih').forEach(b => { b.disabled = true; });
    ctx.selesai(`Tabel frekuensi sudah jadi! Jumlah semua frekuensi = ${p.data.length}. Yang paling banyak dipilih adalah **${modus}**.`);
  };
  host.querySelectorAll('.tr-pilih').forEach(b => b.addEventListener('click', () => {
    if (st.i >= p.data.length) return;
    const k = b.dataset.k;
    if (k !== p.data[st.i]) { ctx.sfx.no(); goyang(b); ctx.ucap(`Eh, jawabannya **${nama(p.data[st.i])}**, bukan ${nama(k)}.`, 'think'); return; }
    st.hit[k]++; st.i++;
    ctx.sfx.tap();
    host.querySelector(`tr[data-k="${k}"] .tr-garis-sel`).innerHTML = turusSVG(st.hit[k]);
    ctx.ucap(st.hit[k] % 5 === 0 ? 'Turus kelima dibuat **miring** mencoret empat garis, supaya mudah dihitung per lima.' : '');
    tampilAntre();
    if (st.i >= p.data.length) selesai();
  }));
  tampilAntre();
}

// ---------- diagram batang ----------
function kerangka(p, tinggiMaks) {
  const W = 360, H = 240, ml = 40, mr = 10, mt = 16, mb = 34, pw = W - ml - mr, ph = H - mt - mb;
  const y = v => mt + ph - (v / tinggiMaks) * ph;
  const bw = pw / p.label.length;
  let g = '';
  for (let v = 0; v <= tinggiMaks; v += p.langkah || 1) g += `<line x1="${ml}" y1="${y(v)}" x2="${W - mr}" y2="${y(v)}" class="sv-grid"/><text x="${ml - 6}" y="${y(v) + 4}" class="sv-t sv-sm" text-anchor="end">${v}</text>`;
  g += `<line x1="${ml}" y1="${mt}" x2="${ml}" y2="${mt + ph}" class="sv-axis"/><line x1="${ml}" y1="${mt + ph}" x2="${W - mr}" y2="${mt + ph}" class="sv-axis"/>`;
  p.label.forEach((l, i) => { g += `<text x="${ml + i * bw + bw / 2}" y="${H - mb + 18}" class="sv-t sv-sm" text-anchor="middle">${l}</text>`; });
  return { W, H, ml, mt, ph, bw, y, g };
}

/**
 * Susun diagram batang dari tabel: seret batang (atau tombol +/−, panah atas/bawah).
 * param: { judul, satuan, label: [...], nilai: [...], maks, langkah }
 */
export function buatDiagram(host, p, ctx) {
  const maks = p.maks || Math.max(...p.nilai) + 2, step = p.langkah || 1;
  const v = p.label.map(() => 0);
  const k = kerangka(p, maks);
  host.innerHTML = `<div class="wg wg-diagram">
    <table class="tbl db-tabel"><caption>${p.judul}</caption><tbody><tr><th>${p.kolom || 'Nama'}</th>${p.label.map(l => `<td>${l}</td>`).join('')}</tr><tr><th>${p.satuan}</th>${p.nilai.map(n => `<td>${n}</td>`).join('')}</tr></tbody></table>
    <svg class="db-svg" viewBox="0 0 ${k.W} ${k.H}" role="group" aria-label="Diagram batang yang bisa diatur">${k.g}${p.label.map((l, i) => `<g class="db-batang" data-i="${i}" tabindex="0" role="slider" aria-label="${l}" aria-valuemin="0" aria-valuemax="${maks}" aria-valuenow="0"><rect class="db-area" x="${k.ml + i * k.bw}" y="${k.mt}" width="${k.bw}" height="${k.ph}"/><rect class="db-isi b${(i % 5) + 1}" x="${k.ml + i * k.bw + k.bw * 0.18}" width="${k.bw * 0.64}" rx="4"/><text class="db-nilai sv-t" x="${k.ml + i * k.bw + k.bw / 2}" text-anchor="middle"></text></g>`).join('')}</svg>
    <div class="db-tombol">${p.label.map((l, i) => `<span><button type="button" class="btn btn-sm" data-i="${i}" data-d="-1" aria-label="Turunkan batang ${l}">−</button><button type="button" class="btn btn-sm" data-i="${i}" data-d="1" aria-label="Naikkan batang ${l}">+</button></span>`).join('')}</div>
  </div>`;
  const svg = host.querySelector('.db-svg');
  const gambar = i => {
    const g = svg.querySelector(`.db-batang[data-i="${i}"]`), isi = g.querySelector('.db-isi'), t = g.querySelector('.db-nilai');
    isi.setAttribute('y', k.y(v[i])); isi.setAttribute('height', k.mt + k.ph - k.y(v[i]));
    t.setAttribute('y', k.y(v[i]) - 6); t.textContent = v[i] ? v[i] + (v[i] === p.nilai[i] ? ' ✓' : '') : '';
    g.classList.toggle('tepat', v[i] === p.nilai[i]);
    g.setAttribute('aria-valuenow', v[i]);
  };
  const cek = () => {
    const benar = v.filter((x, i) => x === p.nilai[i]).length;
    if (benar === v.length) ctx.selesai(`Diagram batangnya sudah benar! Tinggi setiap batang sama dengan data di tabel.`);
    else ctx.ucap(benar ? `${benar} dari ${v.length} batang sudah tepat.` : '');
  };
  const atur = (i, nilai) => {
    const n = batas(Math.round(nilai / step) * step, 0, maks);
    if (n === v[i]) return;
    v[i] = n; gambar(i); cek();
  };
  svg.querySelectorAll('.db-batang').forEach(g => {
    const i = +g.dataset.i;
    seret(g, { onGerak: e => { const pt = titikSVG(svg, e); atur(i, ((k.mt + k.ph - pt.y) / k.ph) * maks); }, onSelesai: () => ctx.sfx.tap() });
    g.addEventListener('keydown', e => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { e.preventDefault(); atur(i, v[i] + step); }
      if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { e.preventDefault(); atur(i, v[i] - step); }
    });
    gambar(i);
  });
  host.querySelectorAll('.db-tombol button').forEach(b => b.addEventListener('click', () => { ctx.sfx.tap(); atur(+b.dataset.i, v[+b.dataset.i] + step * Number(b.dataset.d)); }));
}

/**
 * Baca diagram batang: ketuk batang tertinggi, terendah, lalu pilih selisihnya.
 * param: { judul, satuan, label, nilai, maks, langkah }
 */
export function bacaDiagram(host, p, ctx) {
  const maks = p.maks || Math.max(...p.nilai) + 2;
  const k = kerangka(p, maks);
  const iMaks = p.nilai.indexOf(Math.max(...p.nilai)), iMin = p.nilai.indexOf(Math.min(...p.nilai));
  const selisih = p.nilai[iMaks] - p.nilai[iMin];
  const pilihan = [...new Set([selisih, selisih + (p.langkah || 1), Math.max(1, selisih - (p.langkah || 1)), p.nilai[iMaks] + p.nilai[iMin]])].sort((a, b) => a - b);
  let tahap = 0;
  host.innerHTML = `<div class="wg wg-baca">
    <p class="db-tanya" aria-live="polite"></p>
    <svg class="db-svg" viewBox="0 0 ${k.W} ${k.H}" role="group" aria-label="${p.judul}"><text x="${k.W / 2}" y="12" class="sv-t sv-sm" text-anchor="middle">${p.judul} (${p.satuan})</text>${k.g}${p.label.map((l, i) => `<g class="db-batang baca" data-i="${i}" tabindex="0" role="button" aria-label="${l}"><rect class="db-area" x="${k.ml + i * k.bw}" y="${k.mt}" width="${k.bw}" height="${k.ph}"/><rect class="db-isi b${(i % 5) + 1}" x="${k.ml + i * k.bw + k.bw * 0.18}" y="${k.y(p.nilai[i])}" width="${k.bw * 0.64}" height="${k.mt + k.ph - k.y(p.nilai[i])}" rx="4"/><text class="db-nilai sv-t" x="${k.ml + i * k.bw + k.bw / 2}" y="${k.y(p.nilai[i]) - 6}" text-anchor="middle"></text></g>`).join('')}</svg>
    <div class="db-pilihan" hidden>${pilihan.map(n => `<button type="button" class="btn" data-n="${n}">${n} ${p.satuan}</button>`).join('')}</div>
  </div>`;
  const tanya = host.querySelector('.db-tanya'), pil = host.querySelector('.db-pilihan');
  const TAHAP = ['Ketuk batang yang <b>paling tinggi</b>.', 'Sekarang ketuk batang yang <b>paling rendah</b>.', `Berapa <b>selisih</b> ${p.label[iMaks]} dan ${p.label[iMin]}?`];
  const lanjut = () => { tanya.innerHTML = TAHAP[tahap]; pil.hidden = tahap < 2; if (tahap === 2) animasi(pil, [{ opacity: 0 }, { opacity: 1 }]); };
  const tunjuk = i => {
    const g = host.querySelector(`.db-batang[data-i="${i}"]`);
    g.querySelector('.db-nilai').textContent = p.nilai[i];
    g.classList.add('dibaca');
    letup(g.querySelector('.db-nilai'));
  };
  const ketuk = i => {
    tunjuk(i);
    if (tahap > 1) return;
    const target = tahap === 0 ? iMaks : iMin;
    if (i === target) { ctx.sfx.ok(); host.querySelector(`.db-batang[data-i="${i}"]`).classList.add(tahap === 0 ? 'tertinggi' : 'terendah'); ctx.ucap(`Betul, ${p.label[i]}: **${p.nilai[i]} ${p.satuan}**. Lihat ujung batangnya, lalu tarik garis ke angka di sumbu kiri.`, 'cheer'); tahap++; lanjut(); }
    else { ctx.sfx.no(); ctx.ucap(`${p.label[i]} ${p.nilai[i]} ${p.satuan}. Masih ada yang ${tahap === 0 ? 'lebih tinggi' : 'lebih rendah'}!`, 'think'); }
  };
  host.querySelectorAll('.db-batang').forEach(g => {
    g.addEventListener('click', () => ketuk(+g.dataset.i));
    g.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ketuk(+g.dataset.i); } });
  });
  pil.querySelectorAll('button').forEach(b => b.addEventListener('click', () => {
    if (+b.dataset.n === selisih) { pil.querySelectorAll('button').forEach(x => { x.disabled = true; }); b.classList.add('btn-go'); ctx.selesai(`Tepat! ${p.nilai[iMaks]} − ${p.nilai[iMin]} = **${selisih} ${p.satuan}**.`); }
    else { ctx.sfx.no(); goyang(b); ctx.ucap('Selisih artinya dikurangi: nilai terbesar dikurangi nilai terkecil.', 'think'); }
  }));
  lanjut();
}

/**
 * Lengkapi piktogram: tambah ikon utuh / setengah sampai setiap baris sesuai data.
 * param: { judul, ikon: 'buku'|'bunga'|'bintang'|'ikan', per, satuan, baris: [{label, nilai}] }
 */
export function piktogram(host, p, ctx) {
  const n = p.baris.map(() => 0); // dalam satuan setengah ikon
  host.innerHTML = `<div class="wg wg-picto">
    <div class="pk-legenda">${icoSVG(p.ikon).replace('<svg', '<svg width="30" height="30"')} = ${p.per} ${p.satuan} <span class="muted">·</span> ${icoSVG(p.ikon, true).replace('<svg', '<svg width="30" height="30"')} = ${p.per / 2} ${p.satuan}</div>
    <table class="tbl pk-tabel"><caption>${p.judul}</caption><tbody>${p.baris.map((b, i) => `<tr data-i="${i}"><th>${b.label}<small>${b.nilai} ${p.satuan}</small></th><td class="pk-ikon"></td><td class="pk-aksi"><button type="button" class="btn btn-sm" data-i="${i}" data-d="2" aria-label="Tambah satu ikon untuk ${b.label}">+1</button><button type="button" class="btn btn-sm" data-i="${i}" data-d="1" aria-label="Tambah setengah ikon untuk ${b.label}">+½</button><button type="button" class="btn btn-sm" data-i="${i}" data-d="-1" aria-label="Kurangi setengah ikon untuk ${b.label}">−</button></td></tr>`).join('')}</tbody></table>
  </div>`;
  const gambar = i => {
    const sel = host.querySelector(`tr[data-i="${i}"] .pk-ikon`);
    const utuh = Math.floor(n[i] / 2), setengah = n[i] % 2;
    sel.innerHTML = Array(utuh).fill(icoSVG(p.ikon)).join('') + (setengah ? icoSVG(p.ikon, true) : '');
    const tepat = n[i] * p.per / 2 === p.baris[i].nilai;
    host.querySelector(`tr[data-i="${i}"]`).classList.toggle('tepat', tepat);
    const akhir = sel.lastElementChild;
    if (akhir) letup(akhir);
  };
  host.querySelectorAll('.pk-aksi button').forEach(b => b.addEventListener('click', () => {
    const i = +b.dataset.i;
    n[i] = batas(n[i] + Number(b.dataset.d), 0, 16);
    ctx.sfx.tap();
    gambar(i);
    const nilai = n[i] * p.per / 2, target = p.baris[i].nilai;
    const tepat = n.filter((x, j) => x * p.per / 2 === p.baris[j].nilai).length;
    if (tepat === n.length) ctx.selesai(`Piktogramnya lengkap! Ingat: 1 ikon = ${p.per} ${p.satuan}, jadi setengah ikon = ${p.per / 2} ${p.satuan}.`);
    else if (nilai > target) ctx.ucap(`${p.baris[i].label} kelebihan: sekarang ${nilai} ${p.satuan}, seharusnya ${target}.`, 'wow');
    else if (nilai === target) ctx.ucap(`${p.baris[i].label} sudah pas: ${target} ${p.satuan} ✓`, 'cheer');
    else ctx.ucap(`${p.baris[i].label}: ${nilai} dari ${target} ${p.satuan}.`);
  }));
  p.baris.forEach((_, i) => gambar(i));
}

export const WIDGET_DATA = { turus, buatDiagram, bacaDiagram, piktogram };
